import React, { useState } from 'react';
import { MapPin, DollarSign, Dumbbell, Clock, Star, ChevronDown, ChevronUp, ExternalLink, Users, Zap, ArrowRight } from 'lucide-react';
import { LeadCaptureForm } from '../forms/LeadCaptureForm';

const GymCard = ({ gym, isExpanded, onToggle }) => (
  <div
    className={`border transition-all duration-300 overflow-hidden ${gym.featured ? 'border-[#E63946] bg-[#E63946]/5 ring-1 ring-[#E63946]/20' : isExpanded ? 'border-[#E63946] bg-zinc-900/80' : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'}`}
    data-testid={`gym-card-${gym.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
  >
    {gym.featured && (
      <div className="bg-[#E63946] text-white text-[10px] font-bold uppercase tracking-widest text-center py-1 px-2" data-testid="featured-badge">
        Featured Gym
      </div>
    )}
    <button onClick={onToggle} className="w-full text-left p-5 flex items-start gap-4">
      <div className={`w-10 h-10 ${gym.featured ? 'bg-[#E63946]/20 border-[#E63946]/50' : 'bg-[#E63946]/10 border-[#E63946]/30'} border flex items-center justify-center flex-shrink-0 mt-0.5`}>
        <Dumbbell className="w-5 h-5 text-[#E63946]" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-white font-semibold text-lg leading-tight">{gym.name}</h3>
        <p className="text-zinc-500 text-sm mt-1 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {gym.neighborhood || gym.address}
        </p>
        {gym.rating && (
          <div className="flex items-center gap-1 mt-1">
            <Star className="w-3 h-3 text-[#FFD100] fill-[#FFD100]" />
            <span className="text-[#FFD100] text-xs font-medium">{gym.rating}</span>
            {gym.reviews && <span className="text-zinc-600 text-xs">({typeof gym.reviews === 'number' && gym.reviews > 999 ? `${(gym.reviews/1000).toFixed(0)}k` : gym.reviews} reviews)</span>}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 mt-1">
        {isExpanded ? <ChevronUp className="w-5 h-5 text-[#E63946]" /> : <ChevronDown className="w-5 h-5 text-zinc-500" />}
      </div>
    </button>

    {isExpanded && (
      <div className="px-5 pb-5 space-y-4 border-t border-zinc-800 pt-4">
        {gym.description && <p className="text-zinc-400 text-sm leading-relaxed">{gym.description}</p>}

        {/* Coaches / Instructor Spotlight */}
        {gym.coaches && gym.coaches.length > 0 && (
          <div className="bg-zinc-800/50 p-3 rounded" data-testid="gym-coaches">
            <div className="flex items-center gap-1.5 mb-2"><Users className="w-3 h-3 text-[#E63946]" /><span className="text-[#E63946] text-xs uppercase tracking-wider font-semibold">HYROX Coaches</span></div>
            {gym.coaches.map(c => (
              <div key={c.name} className="flex items-center gap-2 mb-1.5 last:mb-0">
                <span className="text-white text-sm font-medium">{c.name}</span>
                {c.handle && <a href={c.instagram || `https://instagram.com/${c.handle.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="text-zinc-500 text-xs hover:text-[#E63946] transition-colors">{c.handle}</a>}
                {c.title && <span className="text-zinc-600 text-xs">— {c.title}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Discount Code */}
        {gym.discountCode && (
          <div className="bg-[#E63946]/10 border border-[#E63946]/30 p-3 rounded" data-testid="gym-discount">
            <p className="text-[#E63946] text-xs uppercase tracking-wider font-semibold mb-1">Discount Code</p>
            <p className="text-white text-sm">Use code <span className="bg-[#E63946] text-white px-2 py-0.5 font-bold text-xs tracking-wider">{gym.discountCode}</span> at checkout</p>
          </div>
        )}

        {/* Class Schedule */}
        {gym.classSchedule && Object.keys(gym.classSchedule).length > 0 && (
          <div data-testid="gym-class-schedule">
            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">HYROX Class Schedule</p>
            <div className="space-y-1">
              {Object.entries(gym.classSchedule).map(([day, times]) => (
                <div key={day} className="flex items-center gap-2 text-sm">
                  <span className="text-zinc-400 w-24">{day}</span>
                  <span className="text-white">{Array.isArray(times) ? times.join(', ') : times}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {gym.pricing && (
            <div className="bg-zinc-800/50 p-3 rounded">
              <div className="flex items-center gap-1.5 mb-1"><DollarSign className="w-3 h-3 text-[#E63946]" /><span className="text-zinc-500 text-xs uppercase tracking-wider">Pricing</span></div>
              <p className="text-white text-sm font-medium">{gym.pricing}</p>
            </div>
          )}
          {gym.hours && (
            <div className="bg-zinc-800/50 p-3 rounded">
              <div className="flex items-center gap-1.5 mb-1"><Clock className="w-3 h-3 text-[#00F0FF]" /><span className="text-zinc-500 text-xs uppercase tracking-wider">Hours</span></div>
              <p className="text-white text-sm font-medium">{gym.hours}</p>
            </div>
          )}
        </div>

        {gym.equipment && gym.equipment.length > 0 && (
          <div>
            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">Equipment & Features</p>
            <div className="flex flex-wrap gap-1.5">
              {gym.equipment.map(eq => (
                <span key={eq} className="px-2 py-1 text-xs bg-zinc-800 text-zinc-300 border border-zinc-700">{eq}</span>
              ))}
            </div>
          </div>
        )}

        {gym.hyrox_specific && gym.hyrox_specific.length > 0 && (
          <div>
            <p className="text-[#E63946] text-xs uppercase tracking-wider mb-2">HYROX-Specific</p>
            <div className="flex flex-wrap gap-1.5">
              {gym.hyrox_specific.map(hs => (
                <span key={hs} className="px-2 py-1 text-xs bg-[#E63946]/10 text-[#E63946] border border-[#E63946]/30">{hs}</span>
              ))}
            </div>
          </div>
        )}

        {gym.website && (
          <a href={gym.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#E63946] text-sm hover:underline mt-2">
            Visit Website <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    )}
  </div>
);

export default function GymDirectoryLayout({ article }) {
  const gyms = article.gyms || [];
  const [expandedGym, setExpandedGym] = useState(gyms.length > 0 ? 0 : null);
  const [filter, setFilter] = useState('all');

  const categories = ['all', ...new Set(gyms.map(g => g.type || 'general'))];
  const filtered = filter === 'all' ? gyms : gyms.filter(g => (g.type || 'general') === filter);
  // Sort featured gyms to top
  const sorted = [...filtered].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="gym-directory-layout">
      {/* Main Content */}
      <div className="lg:col-span-7">
        <div
          className="article-content prose prose-invert max-w-none mb-10"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* Lead capture CTA */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-[#E63946]/30 p-8 my-10">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="w-6 h-6 text-[#E63946]" />
            <h3 className="text-white text-xl font-heading">Get the Free HYROX Training Guide</h3>
          </div>
          <p className="text-zinc-400 text-sm mb-4">12-week race preparation plan with station-specific workouts, nutrition timing, and recovery protocols.</p>
          <LeadCaptureForm
            source={`hyrox_guide_${article.slug}`}
            variant="inline"
            buttonText="Send Me the Guide"
            interests={['hyrox_training_guide']}
          />
        </div>
      </div>

      {/* Sidebar — Gym Directory */}
      <div className="lg:col-span-5" data-testid="gym-sidebar">
        <div className="sticky top-24">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white text-xl font-heading flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#E63946]" />
              Gym Directory ({filtered.length})
            </h2>
          </div>

          {/* Type filters */}
          {categories.length > 2 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-3 py-1 text-xs uppercase tracking-wider transition-all border ${filter === cat ? 'bg-[#E63946]/10 border-[#E63946] text-white' : 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:border-zinc-500'}`}
                  data-testid={`gym-filter-${cat}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {sorted.map((gym, i) => (
              <GymCard
                key={gym.name}
                gym={gym}
                isExpanded={expandedGym === i}
                onToggle={() => setExpandedGym(expandedGym === i ? null : i)}
              />
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-6 p-4 bg-zinc-900/50 border border-zinc-800 text-center">
            <p className="text-zinc-500 text-xs mb-2">Know a gym that should be listed?</p>
            <a href="/contact" className="text-[#E63946] text-sm font-medium inline-flex items-center gap-1 hover:underline">
              Submit a Gym <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
